import { Button, Icon, PageHeader, Restricted, Select } from '@components';
import { usePermissionContext } from '@contexts/PermissionProvider';
import CollaboratorExpenseAPICaller from '@services/api/collaboratorExpense';
import CostCenterAPICaller from '@services/api/costCenter';
import { CostCenter } from 'interfaces/CostCenter';
import { EstimateMatrixRow } from 'interfaces/CollaboratorExpense';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import { formatCurrency } from 'utils/string/currency';
import './estimate.css';

const MONTHS: { key: string; label: string }[] = [
  { key: 'january', label: 'Jan' },
  { key: 'february', label: 'Fev' },
  { key: 'march', label: 'Mar' },
  { key: 'april', label: 'Abr' },
  { key: 'may', label: 'Mai' },
  { key: 'june', label: 'Jun' },
  { key: 'july', label: 'Jul' },
  { key: 'august', label: 'Ago' },
  { key: 'september', label: 'Set' },
  { key: 'october', label: 'Out' },
  { key: 'november', label: 'Nov' },
  { key: 'december', label: 'Dez' },
];

const centsToCurrency = (cents?: number) =>
  formatCurrency((Number(cents) || 0) / 100) ?? '';
const centsToNumber = (cents?: number) => (Number(cents) || 0) / 100;
const floatToCents = (float: number) => Math.round(float * 100);

const ALL = 'all';

export default function BudgetEstimatePage() {
  const [searchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [costCenterId, setCostCenterId] = useState<string>(ALL);
  const [rows, setRows] = useState<EstimateMatrixRow[]>([]);
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all');
  const [isLoading, setIsLoading] = useState(false);
  const { hasPermission } = usePermissionContext();
  const canUpdate = hasPermission('collaborator_expenses', 'update');

  useEffect(() => {
    CostCenterAPICaller.listAll().then((values) =>
      setCostCenters(values.result || [])
    );
  }, []);

  const loadMatrix = useCallback(() => {
    setIsLoading(true);
    CollaboratorExpenseAPICaller.matrix(
      year,
      costCenterId === ALL ? undefined : costCenterId
    )
      .then((data) => setRows(data))
      .catch((error) => {
        // Keeping the previous year's payroll on screen after a failed load
        // would read as this year's figures.
        setRows([]);
        const message =
          error?.response?.data?.message ||
          'Erro ao carregar a estimativa de colaboradores.';
        toast.error(message);
      })
      .finally(() => setIsLoading(false));
  }, [year, costCenterId]);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const pendingRef = useRef<Record<string, number>>({});
  const dirtyRef = useRef<Set<string>>(new Set());

  const setCell = (
    collaboratorId: string,
    month: string,
    field: 'compensationCents' | 'healthPlanCents',
    cents: number
  ) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.collaboratorId !== collaboratorId) return row;
        const current = row.months[month] || {
          compensationCents: 0,
          healthPlanCents: 0,
        };
        return {
          ...row,
          months: {
            ...row.months,
            [month]: { ...current, [field]: cents },
          },
        };
      })
    );
    CollaboratorExpenseAPICaller.upsertCell({
      collaboratorId,
      year,
      month,
      [field]: cents,
    }).catch((error) => {
      const message = error?.response?.data?.message || 'Erro ao salvar valor.';
      toast.error(message);
      loadMatrix();
    });
  };

  const applyForward = (
    collaboratorId: string,
    month: string,
    field: 'compensationCents' | 'healthPlanCents',
    cents: number
  ) => {
    CollaboratorExpenseAPICaller.applyFromMonth({
      collaboratorId,
      year,
      month,
      [field]: cents,
    })
      .then(() => {
        toast.success('Valor aplicado deste mês em diante.');
        loadMatrix();
      })
      .catch(() => toast.error('Erro ao aplicar.'));
  };

  const startNewYear = () => {
    CollaboratorExpenseAPICaller.startYear({
      previousYear: year - 1,
      newYear: year,
    })
      .then(() => {
        toast.success(`Ano ${year} iniciado a partir de dezembro/${year - 1}.`);
        loadMatrix();
      })
      .catch(() => toast.error('Erro ao iniciar o ano.'));
  };

  const HISTORY_START = 2025;
  const yearOptions = Array.from(
    { length: currentYear + 1 - HISTORY_START + 1 },
    (_, i) => HISTORY_START + i
  ).map((y) => ({ label: String(y), value: String(y) }));
  const costCenterOptions = [
    { label: 'Todos os centros de custo', value: ALL },
    ...costCenters.map((c) => ({ label: c.name, value: c.id as string })),
  ];
  const selectedCostCenter = costCenterOptions.find(
    (o) => o.value === costCenterId
  );
  const filteredRows = rows
    .filter((r) =>
      search.trim()
        ? r.name.toLowerCase().includes(search.trim().toLowerCase())
        : true
    )
    .filter((r) => (statusFilter === 'all' ? true : r.status === statusFilter));

  const statusOptions = [
    { label: 'Todos', value: 'all' },
    { label: 'Ativo', value: 'active' },
    { label: 'Inativo', value: 'inactive' },
  ];

  // The year has no data at all yet — offer to seed it from last December.
  // `rows` always has one entry per collaborator (they exist independently of
  // any year's data), so "empty" means every row's `months` is `{}` (the API
  // only puts an entry there when a CollaboratorExpense record exists for
  // that collaborator/year). Judged only under "Todos os centros de custo"
  // since costCenterId scopes the query itself — a cost-center-filtered view
  // could look empty while the year has data elsewhere.
  const yearIsEmpty =
    !isLoading &&
    costCenterId === ALL &&
    rows.every((r) => Object.keys(r.months).length === 0);

  return (
    <div className="p-s-300">
      <PageHeader title="Estimativa de folha" />

      <div
        className="estimate-filters d-flex mt-s-200"
        style={{ gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <div style={{ minWidth: 220 }}>
          <div className="form-input__container">
            <label className="form-input__label">Colaborador</label>
            <div className="form-input__addons__container">
              <input
                className="form-input"
                placeholder="Buscar por nome"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div style={{ minWidth: 280 }}>
          <Select
            label="Centro de custo"
            value={selectedCostCenter}
            onSelect={(option: { label: string; value: string }) =>
              setCostCenterId(option.value)
            }
            options={costCenterOptions}
          />
        </div>
        <div style={{ minWidth: 140 }}>
          <Select
            label="Ano"
            value={{ label: String(year), value: String(year) }}
            onSelect={(option: { label: string; value: string }) =>
              setYear(Number(option.value))
            }
            options={yearOptions}
          />
        </div>
        <div style={{ minWidth: 140 }}>
          <Select
            label="Status"
            value={statusOptions.find((o) => o.value === statusFilter)}
            onSelect={(option: { label: string; value: string }) =>
              setStatusFilter(option.value as 'all' | 'active' | 'inactive')
            }
            options={statusOptions}
          />
        </div>
        <Restricted context="collaborator_expenses" resource="create">
          <Button
            design="outlined"
            suffixes={<Icon>event_repeat</Icon>}
            disabled={!yearIsEmpty}
            title={
              yearIsEmpty
                ? undefined
                : 'Só disponível quando o ano ainda não tem nenhum dado lançado.'
            }
            onClick={startNewYear}
          >
            Iniciar ano de dezembro anterior
          </Button>
        </Restricted>
      </div>

      <div className="estimate-legend mt-s-200">
        <span className="legend-item">
          <span className="apply-forward legend-icon">
            <Icon>double_arrow</Icon>
          </span>
          aplica o valor deste mês até dezembro (aumento no meio do ano)
        </span>
        <span className="legend-item">
          <span className="blocked-swatch">—</span>
          mês bloqueado (antes da admissão ou após o desligamento)
        </span>
      </div>

      <div className="estimate-matrix-wrapper mt-s-100">
        {isLoading && rows.length === 0 ? (
          <p className="p-s-200">Carregando...</p>
        ) : (
          <table className="estimate-matrix">
            <thead>
              <tr>
                <th className="sticky-col col-name" rowSpan={2}>
                  Nome do colaborador
                </th>
                <th rowSpan={2}>Status</th>
                <th rowSpan={2}>Centro de custo</th>
                {MONTHS.map((m) => (
                  <th key={m.key} colSpan={3} className="month-group">
                    {m.label}
                  </th>
                ))}
              </tr>
              <tr>
                {MONTHS.map((m) => (
                  <Fragment key={m.key}>
                    <th>A. Médica</th>
                    <th>Produção</th>
                    <th>Total</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={3 + MONTHS.length * 3} className="empty">
                    Nenhum colaborador encontrado.
                  </td>
                </tr>
              )}
              {filteredRows.map((row) => (
                <tr key={row.collaboratorId}>
                  <td className="sticky-col col-name">{row.name}</td>
                  <td style={{ textAlign: 'left' }}>
                    <span
                      style={{
                        color:
                          row.status === 'inactive' ? '#e53935' : '#2e7d32',
                        fontWeight: 600,
                      }}
                    >
                      {row.status === 'inactive' ? 'Inativo' : 'Ativo'}
                    </span>
                  </td>
                  <td>{row.costCenterName}</td>
                  {MONTHS.map((m, monthIndex) => {
                    const cell = row.months[m.key] || {
                      compensationCents: 0,
                      healthPlanCents: 0,
                    };
                    const total = cell.compensationCents + cell.healthPlanCents;
                    const blockedBefore =
                      monthIndex < row.firstAllowedMonthIndex;
                    const blockedAfter =
                      monthIndex > row.lastEditableMonthIndex;
                    const blocked = blockedBefore || blockedAfter;
                    const blockedTitle = blockedAfter
                      ? 'Após o desligamento'
                      : 'Antes da admissão';
                    if (blocked) {
                      return (
                        <Fragment key={m.key}>
                          <td className="blocked-cell" title={blockedTitle}>
                            —
                          </td>
                          <td className="blocked-cell" title={blockedTitle}>
                            —
                          </td>
                          <td className="blocked-cell">—</td>
                        </Fragment>
                      );
                    }
                    return (
                      <Fragment key={m.key}>
                        <td>
                          <div className="cell-wrap">
                            <span className="currency-prefix">R$</span>
                            <NumericFormat
                              className="estimate-input"
                              key={`h-${row.collaboratorId}-${m.key}-${cell.healthPlanCents}`}
                              value={centsToNumber(cell.healthPlanCents)}
                              thousandSeparator="."
                              decimalSeparator=","
                              decimalScale={2}
                              fixedDecimalScale
                              disabled={!canUpdate}
                              onValueChange={({ floatValue }) => {
                                if (!canUpdate) return;
                                const key = `h-${row.collaboratorId}-${m.key}`;
                                pendingRef.current[key] = floatToCents(
                                  floatValue ?? 0
                                );
                                dirtyRef.current.add(key);
                              }}
                              onBlur={() => {
                                if (!canUpdate) return;
                                const key = `h-${row.collaboratorId}-${m.key}`;
                                if (!dirtyRef.current.has(key)) return;
                                dirtyRef.current.delete(key);
                                setCell(
                                  row.collaboratorId,
                                  m.key,
                                  'healthPlanCents',
                                  pendingRef.current[key] ??
                                    cell.healthPlanCents
                                );
                              }}
                            />
                            {canUpdate && (
                              <button
                                type="button"
                                className="apply-forward"
                                title="Aplicar deste mês em diante"
                                onMouseDown={() =>
                                  dirtyRef.current.delete(
                                    `h-${row.collaboratorId}-${m.key}`
                                  )
                                }
                                onClick={() =>
                                  applyForward(
                                    row.collaboratorId,
                                    m.key,
                                    'healthPlanCents',
                                    pendingRef.current[
                                      `h-${row.collaboratorId}-${m.key}`
                                    ] ?? cell.healthPlanCents
                                  )
                                }
                              >
                                <Icon>double_arrow</Icon>
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="cell-wrap">
                            <span className="currency-prefix">R$</span>
                            <NumericFormat
                              className="estimate-input"
                              key={`c-${row.collaboratorId}-${m.key}-${cell.compensationCents}`}
                              value={centsToNumber(cell.compensationCents)}
                              thousandSeparator="."
                              decimalSeparator=","
                              decimalScale={2}
                              fixedDecimalScale
                              disabled={!canUpdate}
                              onValueChange={({ floatValue }) => {
                                if (!canUpdate) return;
                                const key = `c-${row.collaboratorId}-${m.key}`;
                                pendingRef.current[key] = floatToCents(
                                  floatValue ?? 0
                                );
                                dirtyRef.current.add(key);
                              }}
                              onBlur={() => {
                                if (!canUpdate) return;
                                const key = `c-${row.collaboratorId}-${m.key}`;
                                if (!dirtyRef.current.has(key)) return;
                                dirtyRef.current.delete(key);
                                setCell(
                                  row.collaboratorId,
                                  m.key,
                                  'compensationCents',
                                  pendingRef.current[key] ??
                                    cell.compensationCents
                                );
                              }}
                            />
                            {canUpdate && (
                              <button
                                type="button"
                                className="apply-forward"
                                title="Aplicar deste mês em diante"
                                onMouseDown={() =>
                                  dirtyRef.current.delete(
                                    `c-${row.collaboratorId}-${m.key}`
                                  )
                                }
                                onClick={() =>
                                  applyForward(
                                    row.collaboratorId,
                                    m.key,
                                    'compensationCents',
                                    pendingRef.current[
                                      `c-${row.collaboratorId}-${m.key}`
                                    ] ?? cell.compensationCents
                                  )
                                }
                              >
                                <Icon>double_arrow</Icon>
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="total-cell">{centsToCurrency(total)}</td>
                      </Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
